import { BannerItem, H5ConfigJson, LandingPageImage, SelectOption } from '../types';

export type LandingApiMode = 'legacy' | 'cardOrderLp';

/**
 * 灰度期只有“新路由不存在”或“当前链接尚无新版配置”可以回退旧接口。
 * 业务异常在不同网关版本中可能表现为 HTTP 400 + msg，也可能是 HTTP/业务码 404。
 */
export function canFallbackToLegacyLanding(data: any, httpStatus?: number): boolean {
  const message = String(data?.msg ?? data?.message ?? '');
  if (httpStatus != null && httpStatus >= 500) return false;
  return httpStatus === 404
    || data?.code === 404
    || (data?.code === 200 && !data?.data)
    || message.includes('落地页不存在');
}

export function readLandingSearchParams(): Record<string, any> {
  try {
    const parsed = JSON.parse(sessionStorage.getItem('searchParams') ?? '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (error) {
    sessionStorage.removeItem('searchParams');
    return {};
  }
}

/** 本地搜索历史属于非关键缓存；损坏时丢弃，不能让整个搜索页白屏。 */
export function readLandingSearchHistory(): any[] {
  try {
    const parsed = JSON.parse(localStorage.getItem('searchList') ?? '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    localStorage.removeItem('searchList');
    return [];
  }
}

export const DEFAULT_GOODS_TYPE_OPTIONS: SelectOption[] = [
  { label: '号卡', value: 'order', checked: true },
  { label: '流量卡', value: 'IotCard', checked: true },
  { label: '随身WIFI', value: 'equipment', checked: true },
  { label: '宽带', value: 'broadband', checked: true },
];

function normalizeChecked(value: unknown, fallback = true): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['false', '0', 'n', 'no'].includes(normalized)) return false;
    if (['true', '1', 'y', 'yes'].includes(normalized)) return true;
  }
  return fallback;
}

function normalizeStyleLine(value?: string): string {
  if (value === 'DOUBLE') return '1';
  if (value === 'SINGLE') return '0';
  return value === '1' ? '1' : '0';
}

function normalizeSwitch(value?: string): string {
  if (value === 'Y') return '1';
  if (value === 'N') return '0';
  return value === '1' ? '1' : '0';
}

function isBottomBanner(image: LandingPageImage): boolean {
  const position = String(image.imgPosition ?? '').trim().toLowerCase();
  if (position === 'bottom') return true;
  if (position === 'top') return false;
  const type = String(image.imgType ?? '').trim().toLowerCase();
  return type === '2' || type === 'b' || type === 'base' || type.includes('bottom') || type.includes('底');
}

function toImageItem(image: LandingPageImage): BannerItem {
  return {
    label: typeof image.imgUrl === 'string' ? image.imgUrl : '',
    value: getSafeLandingLink(image.imgLink),
  };
}

/**
 * 把 card_order_lp 新接口适配成现役页面一直消费的 H5ConfigJson。
 * 旧接口数据也可以安全传入，因此页面层不需要同时维护两套渲染逻辑。
 */
export function normalizeLandingConfig(
  raw: any,
  options: { legacyEmptyTypesUseDefaults?: boolean } = {},
): H5ConfigJson {
  const images: LandingPageImage[] = Array.isArray(raw?.lpImgs)
    ? raw.lpImgs
        .slice()
        .filter((image: unknown): image is LandingPageImage => !!image && typeof image === 'object')
        .sort((a: LandingPageImage, b: LandingPageImage) => Number(a.imgSort ?? 0) - Number(b.imgSort ?? 0))
    : [];
  const topImages = images.filter((image) => !isBottomBanner(image)).map(toImageItem).filter((item) => !!item.label);
  const bottomImages = images.filter(isBottomBanner).map(toImageItem).filter((item) => !!item.label);
  const legacyTopImages = Array.isArray(raw?.bannerImgList)
    ? raw.bannerImgList.map(toBannerItem).filter((item: BannerItem) => !!item.label)
    : topImages;
  const legacyBottomImages = Array.isArray(raw?.bottomImgList)
    ? raw.bottomImgList.map(toBannerItem).filter((item: BannerItem) => !!item.label)
    : bottomImages;
  const hasRawTypes = Array.isArray(raw?.goodsTypeOptions);
  const legacyHasNoVisibleTypes =
    options.legacyEmptyTypesUseDefaults
    && hasRawTypes
    && !raw.goodsTypeOptions.some((item: SelectOption) =>
      DEFAULT_GOODS_TYPE_OPTIONS.some((defaultItem) => defaultItem.value === item?.value)
      && normalizeChecked(item?.checked));
  const hasConfiguredTypes = hasRawTypes && !legacyHasNoVisibleTypes;

  return {
    ...raw,
    serviceLink: getSafeLandingLink(raw?.serviceLink),
    styleLine: normalizeStyleLine(raw?.styleLine),
    styleTrans: normalizeSwitch(raw?.styleTrans),
    bannerImgList: legacyTopImages,
    bottomImgList: legacyBottomImages,
    goodsTypeOptions: hasConfiguredTypes
      ? raw.goodsTypeOptions.map((item: SelectOption) => ({ ...item }))
      : DEFAULT_GOODS_TYPE_OPTIONS.map((item) => ({ ...item })),
  };
}

/** 只暴露被配置为展示的商品类型，并保留现役 H5 的“全部”入口。 */
export function buildVisibleGoodsTypeOptions(options?: SelectOption[]): SelectOption[] {
  const defaults = new Map(DEFAULT_GOODS_TYPE_OPTIONS.map((item) => [item.value, item]));
  const seen = new Set<string>();
  const visibleOptions: SelectOption[] = [];
  (options ?? DEFAULT_GOODS_TYPE_OPTIONS).forEach((item) => {
    const value = typeof item?.value === 'string' ? item.value : '';
    const defaultItem = defaults.get(value);
    if (!defaultItem || seen.has(value)) return;
    // 历史异常配置按首项生效，避免重复 Tab；首项隐藏时不能被后续重复项重新开启。
    seen.add(value);
    if (!normalizeChecked(item.checked)) return;
    const rawLabel = typeof item.label === 'string' ? item.label.trim() : String(item.label ?? '').trim();
    const label = !rawLabel || (value === 'equipment' && rawLabel === '设备')
      ? defaultItem.label
      : rawLabel;
    visibleOptions.push({ ...item, label, value, checked: true });
  });
  return visibleOptions.length > 0 ? [{ label: '全部', value: 'all', checked: true }, ...visibleOptions] : [];
}

export function toBannerItem(item: string | BannerItem | null | undefined): BannerItem {
  if (typeof item === 'string') return { label: item };
  if (!item || typeof item !== 'object') return { label: '' };
  return {
    label: typeof item.label === 'string' ? item.label : '',
    value: getSafeLandingLink(item.value),
  };
}

function isBareNetworkAddress(value: string): boolean {
  const authority = value.split(/[/?#]/, 1)[0];
  return /^(?:(?:[a-z\d](?:[a-z\d-]*[a-z\d])?\.)+[a-z\d](?:[a-z\d-]*[a-z\d])?|(?:\d{1,3}\.){3}\d{1,3}|localhost)(?::\d{1,5})?$/i.test(
    authority,
  );
}

/**
 * 拒绝可执行脚本 URL，并把运营人员常填的裸域名补成绝对 HTTPS 地址。
 * 业务相对路径、页内锚点和受控 App Scheme 保持原样。
 */
export function getSafeLandingLink(link?: unknown): string | undefined {
  const value = typeof link === 'string' ? link.trim() : '';
  if (!value) return undefined;
  const colonIndex = value.indexOf(':');
  const normalizedScheme = colonIndex < 0
    ? ''
    : value
        .slice(0, colonIndex + 1)
        .split('')
        .filter((character) => character.charCodeAt(0) > 32 && character.charCodeAt(0) !== 127)
        .join('')
        .toLowerCase();
  const blockedSchemes = [['java', 'script:'].join(''), 'data:', ['vb', 'script:'].join('')];
  if (blockedSchemes.includes(normalizedScheme)) return undefined;
  if (value.startsWith('//')) return `https:${value}`;
  if (isBareNetworkAddress(value)) return `https://${value}`;
  return value;
}

/** 兼容 0 号（无数字前缀）、一位数和多位数落地页编号。 */
export function getLandingPageNumber(code?: string | null): string {
  if (!code) return '';
  return code.match(/^\d+/)?.[0] ?? '0';
}

/** URL 中只有两个落地页入口的 code 表示落地页；商品详情页的同名参数表示商品编码。 */
export function isLandingEntryPath(pathname: string): boolean {
  const normalizedPath = pathname.replace(/\/+$/, '').toLowerCase() || '/';
  return normalizedPath === '/' || normalizedPath === '/formhome';
}

/** 搜索页提交后回到原落地页；统一编码参数，兼容无 code 但有嵌入来源的边界场景。 */
export function buildLandingHomePath(params: { code?: string; from?: string; uc?: string }): string {
  const searchParams = new URLSearchParams();
  if (params.code) searchParams.set('code', params.code);
  if (params.from) searchParams.set('from', params.from);
  if (params.uc) searchParams.set('uc', params.uc);
  const query = searchParams.toString();
  const pathname = params.from ? '/formHome' : '/';
  return query ? `${pathname}?${query}` : pathname;
}
